import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export function AuthCallback() {
  const navigate = useNavigate()
  const handledRef = useRef(false)

  useEffect(() => {
    // Prevent handling multiple times
    if (handledRef.current) return

    // Timeout to prevent getting stuck - redirect to login after 10 seconds
    const timeout = setTimeout(() => {
      if (!handledRef.current) {
        console.warn('Auth callback timeout - redirecting to login')
        handledRef.current = true
        // Clear any stale URL hash before redirecting
        if (window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname)
        }
        navigate('/login', { replace: true })
      }
    }, 10000)

    // Listen for auth state changes - this properly handles the OAuth callback
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Only handle once
      if (handledRef.current) return

      // Wait for SIGNED_IN event or check if already signed in
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
        if (session) {
          handledRef.current = true
          clearTimeout(timeout)
          // Clear URL hash to prevent stale token issues on refresh
          if (window.location.hash) {
            window.history.replaceState(null, '', window.location.pathname)
          }
          // Small delay to ensure auth state is fully propagated
          setTimeout(() => {
            navigate('/calculator', { replace: true })
          }, 100)
        }
      } else if (event === 'SIGNED_OUT') {
        handledRef.current = true
        clearTimeout(timeout)
        navigate('/login', { replace: true })
      }
    })

    // Also check immediately in case session already exists (e.g., page refresh)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (handledRef.current) return

      // If no hash in URL and we have a session, we're already logged in
      if (!window.location.hash && session) {
        handledRef.current = true
        clearTimeout(timeout)
        navigate('/calculator', { replace: true })
      } else if (!window.location.hash && !session) {
        // No hash and no session - go to login
        handledRef.current = true
        clearTimeout(timeout)
        navigate('/login', { replace: true })
      }
      // If there's a hash, wait for onAuthStateChange to handle it
    })

    return () => {
      clearTimeout(timeout)
      subscription.unsubscribe()
    }
  }, [navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        <p className="mt-4 text-gray-600 dark:text-gray-400">Completing sign in...</p>
      </div>
    </div>
  )
}
